import type { ScheduleScopeChoice } from "../services/calendar";

const STORAGE_KEY_PREFIX = "scheduleScope";

/** Prefix of a stored staff schedule: `user:<member id>`. */
const USER_SCOPE_PREFIX = "user:";

/** Everyone lands on their personal schedule first (plan Decision 4, no role branching). */
export const DEFAULT_SCOPE: ScheduleScopeChoice = { scope: "mine" };

/** localStorage key for the schedule scope of a specific organization. */
const getStorageKey = (organizationId: string): string => {
  return `${STORAGE_KEY_PREFIX}_${organizationId}`;
};

/** A member id as the API issues them. Anything else in storage is junk, not a coach. */
const MEMBER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Reads a stored value: "all", "mine" or "user:<id>". Anything else is null. */
const parseStored = (value: string | null): ScheduleScopeChoice | null => {
  if (value === "all" || value === "mine") return { scope: value };
  if (value?.startsWith(USER_SCOPE_PREFIX)) {
    const userId = value.slice(USER_SCOPE_PREFIX.length);
    if (MEMBER_ID.test(userId)) return { scope: "user", userId };
  }
  return null;
};

/** The stored form of a choice, or null for one that is not valid. */
const serialize = (choice: ScheduleScopeChoice): string | null => {
  if (choice.scope === "all" || choice.scope === "mine") return choice.scope;
  if (choice.scope === "user" && MEMBER_ID.test(choice.userId)) {
    return `${USER_SCOPE_PREFIX}${choice.userId}`;
  }
  return null;
};

/** Whether two choices show the same schedule. */
export const sameScheduleScope = (a: ScheduleScopeChoice, b: ScheduleScopeChoice): boolean =>
  a.scope === b.scope &&
  (a.scope !== "user" || (b.scope === "user" && a.userId === b.userId));

/**
 * The choice to show once the organization's members are known: a staff schedule for someone who
 * is no longer a member (or is the viewer, whose schedule is My events) falls back to the default.
 */
export const scopeForMembers = (
  choice: ScheduleScopeChoice,
  memberIds: readonly string[],
  currentUserId?: string,
): ScheduleScopeChoice =>
  choice.scope === "user" &&
  (choice.userId === currentUserId || !memberIds.includes(choice.userId))
    ? DEFAULT_SCOPE
    : choice;

/**
 * Read the persisted schedule scope for an organization. Falls back to the
 * personal-first default when there is no org, no stored value, or a value that
 * isn't a known scope. A stored staff schedule is returned as is: whether that
 * coach is still a member is checked against the member list by the caller.
 */
export const getStoredScheduleScope = (
  organizationId: string | null,
): ScheduleScopeChoice => {
  if (!organizationId) {
    return DEFAULT_SCOPE;
  }

  try {
    return parseStored(localStorage.getItem(getStorageKey(organizationId))) ?? DEFAULT_SCOPE;
  } catch (error) {
    console.warn("Failed to load schedule scope from localStorage:", error);
    return DEFAULT_SCOPE;
  }
};

/**
 * Persist the schedule scope for an organization. No-ops without an org id or
 * for a value that isn't a known scope.
 */
export const saveScheduleScope = (
  organizationId: string | null,
  choice: ScheduleScopeChoice,
): void => {
  const stored = choice ? serialize(choice) : null;
  if (!organizationId || stored === null) {
    return;
  }

  try {
    localStorage.setItem(getStorageKey(organizationId), stored);
  } catch (error) {
    console.warn("Failed to save schedule scope to localStorage:", error);
  }
};

/**
 * Clear ALL persisted schedule scopes (every organization). Used on logout so a
 * coach's toggle choice never leaks across users or tenants.
 */
export const clearAllScheduleScopes = (): void => {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn("Failed to clear all schedule scopes from localStorage:", error);
  }
};
